import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import ContactCardMessage from './ContactCardMessage';
import { parseContactCardText } from '../utils/contactCardParser';
import './ConversationDetail.css';

function ConversationDetail() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/v1/analytics/conversations/${conversationId}`);
      setConversation(res.data);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      setError('Errore nel caricamento della conversazione');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '#4CAF50';
      case 'negative': return '#F44336';
      case 'neutral': return '#FFC107';
      default: return '#9E9E9E';
    }
  };

  if (loading) {
    return (
      <div className="container conversation-detail">
        <div className="loading">Caricamento conversazione...</div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="container conversation-detail">
        <div className="error-container">
          <p className="error">{error || 'Conversazione non trovata'}</p>
          <button className="btn-back" onClick={() => navigate('/analytics')}>
            ← Torna ad Analytics
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container conversation-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/analytics')}>
          ← {t('conversationDetail.backToAnalytics')}
        </button>
        <h1>{t('conversationDetail.title')}</h1>
        {/* Live Chat Button - Show for WhatsApp, Instagram DM, and Facebook Messenger conversations */}
        {(conversation.platform?.toLowerCase().includes('whatsapp') || conversation.platform?.toLowerCase().includes('instagram_dm') || conversation.platform?.toLowerCase().includes('facebook_message')) && (
          <Link 
            to={`/live-chat/${conversationId}`} 
            className="btn-live-chat"
          >
            {conversation.platform?.toLowerCase().includes('instagram_dm') ? '📸' : conversation.platform?.toLowerCase().includes('facebook_message') ? '📘' : '💬'} {t('conversationDetail.openLiveChat')}
          </Link>
        )}
      </div>

      {/* Conversation Info Card */}
      <div className="card info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Contatto</span>
            <span className="info-value">{conversation.contact_name || conversation.contact_id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Platform</span>
            <span className={`platform-badge ${conversation.platform?.toLowerCase()}`}>
              {conversation.platform}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Sentiment</span>
            <span 
              className="sentiment-badge" 
              style={{ backgroundColor: getSentimentColor(conversation.overall_sentiment) }}
            >
              {conversation.overall_sentiment || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Sentiment Score</span>
            <span className="info-value">
              {conversation.average_sentiment_score?.toFixed(2) || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Totale Messaggi</span>
            <span className="info-value">{conversation.message_count || 0}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Tempo Risposta Medio</span>
            <span className="info-value">
              {conversation.total_response_time_seconds 
                ? `${Math.round(conversation.total_response_time_seconds / Math.max(1, conversation.message_count))}s`
                : 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Creata il</span>
            <span className="info-value">
              {new Date(conversation.created_at).toLocaleString()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Ultimo Messaggio</span>
            <span className="info-value">
              {conversation.last_message_at 
                ? new Date(conversation.last_message_at).toLocaleString()
                : 'N/A'}
            </span>
          </div>
          {conversation.was_escalated && (
            <div className="info-item full-width">
              <span className="escalated-alert">
                ⚠ Questa conversazione è stata escalata
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Messages Timeline */}
      <div className="card messages-card">
        <h2>Log dei Messaggi</h2>
        <div className="messages-timeline">
          {conversation.messages && conversation.messages.length > 0 ? (
            conversation.messages.map((msg, index) => {
              const isInbound = msg.direction === 'inbound';
              const extraPayload =
                msg.extra_data && msg.extra_data.kind === 'contact_card'
                  ? msg.extra_data
                  : null;
              const cardPayload =
                extraPayload ||
                (msg.message_type === 'contact_card'
                  ? parseContactCardText(msg.message_text)
                  : parseContactCardText(msg.message_text));
              const isContactCard =
                msg.message_type === 'contact_card' || !!cardPayload;
              const senderLabel = isInbound ? 'Contatto' : 'Agente';
              const senderIcon = isInbound ? '👤' : '🤖';

              return (
                <div
                  key={msg.id || index}
                  className={`message-item ${msg.direction}`}
                >
                  <div className="message-timeline-dot">{senderIcon}</div>
                  <div className="message-content-box">
                    <div className="message-meta">
                      <span className="message-sender">{senderLabel}</span>
                      <span className="message-timestamp">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                      {msg.sentiment && (
                        <span
                          className="sentiment-mini"
                          style={{ backgroundColor: getSentimentColor(msg.sentiment) }}
                        >
                          {msg.sentiment} ({msg.sentiment_score?.toFixed(2) || '-'})
                        </span>
                      )}
                    </div>
                    {isContactCard && cardPayload ? (
                      <ContactCardMessage
                        card={cardPayload}
                        direction={isInbound ? 'inbound' : 'outbound'}
                        senderLabel={senderLabel}
                        senderIcon={senderIcon}
                        timestamp={msg.created_at}
                      />
                    ) : (
                      <div className="message-text-content">
                        {msg.message_text}
                      </div>
                    )}
                    {msg.response_time_seconds && (
                      <div className="message-response-time">
                        ⏱ Risposta in {msg.response_time_seconds}s
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-messages">
              Nessun messaggio in questa conversazione
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConversationDetail;
